"use client";

import { User } from "lucide-react";
import { Button } from "@gerama/ui";
import { env } from "@/env";

const RETURN_KEY = "gerama.google.return";
const STATE_KEY = "gerama.google.state";
const NONCE_KEY = "gerama.google.nonce";

/** Same client id the backend lists in `GOOGLE_CLIENT_IDS`. */
export function googleClientId(): string | undefined {
  return env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
}

/**
 * Full-page Google redirect, the same idea as Mayava's `signIn("google")`.
 * The GIS iframe button does not render inside the login dialog, so every
 * step uses this control and the callback page finishes the session.
 */
export function startGoogleSignIn(): void {
  const clientId = googleClientId();
  if (!clientId || typeof window === "undefined") return;

  const nonce = crypto.randomUUID();
  const state = crypto.randomUUID();
  sessionStorage.setItem(RETURN_KEY, `${window.location.pathname}${window.location.search}`);
  sessionStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(NONCE_KEY, nonce);

  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: `${window.location.origin}/auth/google/callback`,
    response_type: "id_token",
    scope: "openid email profile",
    nonce,
    state,
    prompt: "select_account",
  });
  window.location.assign(`https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`);
}

export function readGoogleHandoff(): { idToken: string; returnTo: string } | { error: string } {
  const hash = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const returnedState = hash.get("state");
  const idToken = hash.get("id_token");
  const expectedState = sessionStorage.getItem(STATE_KEY);
  const expectedNonce = sessionStorage.getItem(NONCE_KEY);
  const returnTo = safeReturn(sessionStorage.getItem(RETURN_KEY));
  sessionStorage.removeItem(STATE_KEY);
  sessionStorage.removeItem(NONCE_KEY);
  sessionStorage.removeItem(RETURN_KEY);

  if (hash.get("error")) return { error: "ورود با گوگل لغو شد." };
  if (!idToken || !returnedState || returnedState !== expectedState) {
    return { error: "ورود با گوگل ناموفق بود. دوباره تلاش کنید." };
  }
  if (tokenNonce(idToken) !== expectedNonce) {
    return { error: "ورود با گوگل ناموفق بود. دوباره تلاش کنید." };
  }
  return { idToken, returnTo };
}

function safeReturn(value: string | null): string {
  if (!value || !value.startsWith("/") || value.startsWith("//") || value.startsWith("/auth/google")) {
    return "/";
  }
  if (value.startsWith("/auth/login")) {
    const next = new URL(value, "http://local").searchParams.get("next");
    if (next && next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/auth/")) return next;
    return "/";
  }
  return value;
}

function tokenNonce(idToken: string): string | null {
  const payload = idToken.split(".")[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const json = JSON.parse(atob(padded)) as { nonce?: unknown };
    return typeof json.nonce === "string" ? json.nonce : null;
  } catch {
    return null;
  }
}

export function GoogleSignInButton({ disabled = false }: { disabled?: boolean }) {
  if (!googleClientId()) return null;

  return (
    <Button
      type="button"
      variant="outline"
      className="h-12 w-full rounded-lg px-3 font-light"
      disabled={disabled}
      aria-label="با اکانت گوگل ادامه دهید"
      onClick={startGoogleSignIn}
    >
      <span className="flex w-full items-center justify-between gap-2">
        <span className="flex items-center gap-2">
          <span className="bg-muted text-muted-foreground flex size-8 items-center justify-center rounded-md">
            <User className="size-4" />
          </span>
          با اکانت گوگل ادامه دهید
        </span>
        <GoogleMark />
      </span>
    </Button>
  );
}

function GoogleMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 12 24 12c3.1 0 5.8 1.2 8 3.1l5.7-5.7C34.2 6.1 29.4 4 24 4 16.3 4 9.6 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 10-2 13.6-5.2l-6.3-5.3C29.3 35.1 26.8 36 24 36c-5.3 0-9.7-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-1 2.9-3.1 5.2-5.9 6.5l6.3 5.3C38.2 37.3 44 32 44 24c0-1.2-.1-2.3-.4-3.5z" />
    </svg>
  );
}
